import React, { useState, useEffect } from 'react';
import { Sparkles, Loader2, Plus, X, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'react-hot-toast';

/**
 * EthosSection - Section 1: School Ethos & Mission
 *
 * Fields:
 * - School Motto/Mission (text, pre-filled from college data)
 * - What this looks like in action (textarea, 400 chars)
 * - Buzzwords (dynamic array, 3-5 items with term + rephrasing)
 */
export default function EthosSection({ data, onChange, collegeData }) {
  const [aiLoading, setAiLoading] = useState(false);
  const [researchLoading, setResearchLoading] = useState(false);
  const [showGuidance, setShowGuidance] = useState(false);

  // Initialize with college data on mount
  useEffect(() => {
    if (collegeData?.global?.mission_statement && !data.motto) {
      onChange({
        ...data,
        motto: collegeData.global.mission_statement
      });
    }

    // Auto-populate buzzwords from core_values if empty
    if (collegeData?.global?.core_values && (!data.buzzwords || data.buzzwords.length === 0)) {
      const values = collegeData.global.core_values.split(',').slice(0, 5);
      const buzzwords = values.map(term => ({
        term: term.trim(),
        rephrase: ''
      }));
      onChange({
        ...data,
        buzzwords: buzzwords
      });
    }
  }, [collegeData]);

  // Get AI suggestions for student responses (buzzwords, motto in action)
  const getAISuggestions = async () => {
    // Validate we have college data
    if (!collegeData || !collegeData.id) {
      toast.error('College data not loaded yet. Please wait a moment and try again.');
      console.error('Missing college data:', collegeData);
      return;
    }

    setAiLoading(true);
    try {
      console.log('🎓 Requesting AI suggestions for ethos section:', {
        college_id: collegeData.id,
        has_global_data: !!collegeData.global
      });

      const response = await fetch('/api/why-school/enhance-section', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          college_id: collegeData.id,
          section: 'ethos',
          mode: 'suggestions', // Student response suggestions only
          existing_data: data,
          global_college_data: collegeData.global
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API error:', response.status, errorData);
        throw new Error(errorData.error || 'Failed to get suggestions');
      }

      const suggestions = await response.json();
      console.log('✅ Received suggestions:', suggestions);

      // Update with suggestions
      const updates = { ...data };
      let hasUpdates = false;

      // Only populate buzzwords if student hasn't added any yet
      if (suggestions.buzzwords && suggestions.buzzwords.length > 0 && (!data.buzzwords || data.buzzwords.length === 0)) {
        updates.buzzwords = suggestions.buzzwords.map(b => ({
          term: b.term,
          rephrase: b.suggested_rephrase || ''
        }));
        hasUpdates = true;
      }

      // Only populate motto_in_action if empty
      if (suggestions.motto_in_action && !data.motto_in_action) {
        updates.motto_in_action = suggestions.motto_in_action;
        hasUpdates = true;
      }

      if (hasUpdates) {
        onChange(updates);
        toast.success('AI suggestions added! Review and edit as needed.');
      } else {
        toast.success('You already have content here. AI won\'t overwrite your work!');
      }
    } catch (err) {
      console.error('Error getting AI suggestions:', err);
      toast.error(`Could not load AI suggestions: ${err.message}`);
    } finally {
      setAiLoading(false);
    }
  };

  // Get AI research assistance (populates research panel with verification warning)
  const getAIResearch = async () => {
    // Validate we have college data
    if (!collegeData || !collegeData.id) {
      toast.error('College data not loaded yet. Please wait a moment and try again.');
      return;
    }

    setResearchLoading(true);
    try {
      console.log('🔬 Requesting AI research for ethos section:', {
        college_id: collegeData.id
      });

      const response = await fetch('/api/why-school/enhance-section', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          college_id: collegeData.id,
          section: 'ethos',
          mode: 'research', // Research data generation
          existing_data: data,
          global_college_data: collegeData.global
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to get research');
      }

      const research = await response.json();
      console.log('✅ Received AI research:', research);

      // Show success with warning
      toast.success('AI research loaded. ⚠️ VERIFY all data before using!', {
        duration: 6000,
        icon: '⚠️'
      });

      // Research is saved to collegeData, will appear in research panel
    } catch (err) {
      console.error('Error getting AI research:', err);
      toast.error(`Could not load AI research: ${err.message}`);
    } finally {
      setResearchLoading(false);
    }
  };

  // Add a new buzzword
  const addBuzzword = () => {
    const buzzwords = data.buzzwords || [];
    if (buzzwords.length >= 8) {
      toast.error('Maximum 8 buzzwords allowed');
      return;
    }
    onChange({
      ...data,
      buzzwords: [...buzzwords, { term: '', rephrase: '' }]
    });
  };

  // Update a buzzword
  const updateBuzzword = (index, field, value) => {
    const buzzwords = [...(data.buzzwords || [])];
    buzzwords[index] = { ...buzzwords[index], [field]: value };
    onChange({ ...data, buzzwords });
  };

  // Remove a buzzword
  const removeBuzzword = (index) => {
    const buzzwords = [...(data.buzzwords || [])];
    buzzwords.splice(index, 1);
    onChange({ ...data, buzzwords });
  };

  return (
    <div className="space-y-8">
      {/* Section Guidance */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <button
          onClick={() => setShowGuidance(!showGuidance)}
          className="flex items-center justify-between w-full text-left"
        >
          <div className="flex items-center space-x-2">
            <Info className="w-5 h-5 text-indigo-600" />
            <span className="font-semibold text-indigo-900">Section Guidance</span>
          </div>
          {showGuidance ? (
            <ChevronUp className="w-5 h-5 text-indigo-600" />
          ) : (
            <ChevronDown className="w-5 h-5 text-indigo-600" />
          )}
        </button>

        {showGuidance && (
          <div className="mt-4 space-y-3 text-sm text-indigo-800">
            <p className="font-medium">What This Section Does:</p>
            <p>
              Capture the school's distinctive values and mission. Your goal is to translate
              their official language into concrete examples of what those values look like in practice.
            </p>
            <div className="bg-white rounded p-3 border border-indigo-200">
              <p className="font-medium mb-2">Strong Example:</p>
              <p className="italic">
                Motto: "Mens et Manus" (Mind and Hand)
                <br />
                In action: "At MIT, students don't separate thinking and building—they prototype
                theories in makerspaces and debug hypotheses with oscilloscopes."
              </p>
            </div>
          </div>
        )}
      </div>

      {/* AI Buttons */}
      <div className="flex justify-end gap-3">
        <button
          onClick={getAIResearch}
          disabled={researchLoading}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          title="AI will help identify relevant programs, faculty, and opportunities. You MUST verify all data."
        >
          {researchLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Researching...</span>
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              <span>AI Research Assistant</span>
            </>
          )}
        </button>
        <button
          onClick={getAISuggestions}
          disabled={aiLoading}
          className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          title="AI will suggest how to interpret the school's values and motto"
        >
          {aiLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Loading...</span>
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              <span>Get AI Suggestions</span>
            </>
          )}
        </button>
      </div>

      {/* Field 1: School Motto/Mission */}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          School Motto or Mission Statement
        </label>
        <p className="text-sm text-gray-600 mb-3">
          Type the school's motto or guiding phrase (e.g., <em>Mens et Manus</em>).
        </p>
        <input
          type="text"
          value={data.motto || ''}
          onChange={(e) => onChange({ ...data, motto: e.target.value })}
          placeholder="e.g., Mens et Manus"
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      </div>

      {/* Field 1b: Mission Elaboration (Optional) */}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Mission Elaboration
          <span className="ml-2 text-xs font-normal text-gray-500">(Optional)</span>
        </label>
        <p className="text-sm text-gray-600 mb-3">
          If the school elaborates on their mission statement (especially helpful for Latin mottos or when historical context matters), paste that here.
        </p>
        <textarea
          value={data.mission_elaboration || ''}
          onChange={(e) => onChange({ ...data, mission_elaboration: e.target.value })}
          placeholder="Paste the school's elaboration on their mission statement..."
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y min-h-[100px]"
          style={{ height: 'auto', minHeight: '100px' }}
          onInput={(e) => {
            e.target.style.height = 'auto';
            e.target.style.height = e.target.scrollHeight + 'px';
          }}
        />
      </div>

      {/* Field 2: What this looks like in action */}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          What this motto looks like in action
        </label>
        <p className="text-sm text-gray-600 mb-3">
          Describe what people at this school would be <em>doing</em> if they truly lived this motto.
          Don't just explain what it means—show it through specific behaviors or examples.
        </p>
        <textarea
          value={data.motto_in_action || ''}
          onChange={(e) => onChange({ ...data, motto_in_action: e.target.value })}
          placeholder="Example: &quot;At MIT, students don't separate thinking and building - they do both at once.&quot;"
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y min-h-[100px]"
          style={{ height: 'auto', minHeight: '100px' }}
          onInput={(e) => {
            e.target.style.height = 'auto';
            e.target.style.height = e.target.scrollHeight + 'px';
          }}
        />
      </div>

      {/* Field 3: Buzzwords */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-semibold text-gray-900">
            School Buzzwords
            <span className="ml-2 text-xs font-normal text-gray-500">
              ({(data.buzzwords || []).length}/8)
            </span>
          </label>
          <button
            onClick={addBuzzword}
            disabled={(data.buzzwords || []).length >= 8}
            className="flex items-center space-x-1 text-sm text-indigo-600 hover:text-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            <span>Add Buzzword</span>
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          List key words or ideas you see repeated on their website (e.g., "innovation",
          "community impact", "curiosity"). Then rewrite each in your own words with examples.
        </p>

        {/* Buzzwords List */}
        <div className="space-y-4">
          {(data.buzzwords || []).map((buzzword, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-start space-x-3">
                <div className="flex-1 space-y-3">
                  {/* Term */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Term #{index + 1}
                    </label>
                    <input
                      type="text"
                      value={buzzword.term || ''}
                      onChange={(e) => updateBuzzword(index, 'term', e.target.value)}
                      placeholder="e.g., innovation"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>

                  {/* Rephrase */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Your Interpretation
                    </label>
                    <textarea
                      value={buzzword.rephrase || ''}
                      onChange={(e) => updateBuzzword(index, 'rephrase', e.target.value)}
                      placeholder='e.g., "Innovation means questioning why we build, not just building fast"'
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y min-h-[60px]"
                      style={{ height: 'auto', minHeight: '60px' }}
                      onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                    />
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-gray-500 italic">
                        Suggestion: Keep it concrete and specific
                      </span>
                    </div>
                  </div>
                </div>

                {/* Remove Button */}
                <button
                  onClick={() => removeBuzzword(index)}
                  className="flex-shrink-0 p-2 text-gray-400 hover:text-red-600 transition"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          {/* Empty State */}
          {(!data.buzzwords || data.buzzwords.length === 0) && (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <p className="text-sm text-gray-600 mb-3">No buzzwords added yet</p>
              <button
                onClick={addBuzzword}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
              >
                <Plus className="w-4 h-4" />
                <span>Add Your First Buzzword</span>
              </button>
            </div>
          )}
        </div>

        {/* Minimum requirement note */}
        {data.buzzwords && data.buzzwords.length > 0 && data.buzzwords.length < 3 && (
          <p className="text-sm text-amber-600 mt-2">
            ⚠ Add at least {3 - data.buzzwords.length} more buzzword(s) to complete this section
          </p>
        )}
      </div>

      {/* Example/Tip Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">💡 Pro Tip</h4>
        <p className="text-sm text-blue-800">
          The best buzzword interpretations connect the school's language to concrete behaviors
          or values. Instead of "Stanford values innovation," try "At Stanford, innovation means
          students prototype ideas in garages and fail fast without judgment."
        </p>
      </div>
    </div>
  );
}
